<template>
    <section ref="dropZoneRef" class="advanced-search">
        <div :class="[getWrapperStyle, {'advanced-search-wrapper--soft-shadow': !rounded}]">
            <VJoyWrapper item-stretch align="flex-start" no-margin>
                <DocumentButtonInput v-if="enableUpload && !bottomAlignedActions" v-model="documentUpload" :prompt-term="promptModel" />
                <div class="advanced-search-controls">
                    <div v-if="isDragAndDropOverlayDisplay" class="drop-overlay">
                        <VJoyWrapper direction="column" row-gap="3" no-margin>
                            <div><VJoyIcon name="download" size="medium" /></div>
                            <div class="drop-overlay__text">{{ dragLabel }}</div>
                        </VJoyWrapper>
                    </div>
                    <DocumentPreview
                        v-if="enableUpload && documentUpload"
                        data-testid="document-preview"
                        :document="documentUpload"
                        @delete="documentUpload = undefined"
                    />
                    <VJoyTextarea
                        v-else
                        v-model="promptModel"
                        data-testid="advanced-search-prompt-textarea"
                        class="advanced-search__prompt"
                        :placeholder="t('search.advanced.ai.entrypoint.mode.ai.matching.input.placeholder.rework')"
                        :disabled="disableTextArea"
                        autogrow
                        @keydown="handleKeyDown"
                        @focus="() => (promptTextareaFocused = true)"
                        @blur="() => (promptTextareaFocused = false)"
                    />
                </div>
                <div v-if="matchMedia.min.Xs && !bottomAlignedActions" class="advanced-search-submit">
                    <VJoyButton
                        v-if="showSearchCta"
                        variant="main-ai"
                        size="small"
                        :icon="submitIcon"
                        icon-position="right"
                        class="advanced-search-submit__cta"
                        data-testid="advanced-search-submit-button"
                        :loading="loading"
                        :disabled="!canBeSubmitted"
                        :label="t('search.advanced.ai.entrypoint.mode.ai.matching.cta.label.v2')"
                        @click="emit('submit')"
                    />
                </div>
            </VJoyWrapper>
            <div v-if="bottomAlignedActions && (enableUpload || showSearchCta)" class="advanced-search-footer">
                <template v-if="enableUpload">
                    <button
                        type="button"
                        label="Upload document"
                        class="advanced-search-document-upload-button"
                        data-testid="advanced-search-document-upload-trigger"
                        @click="uploadInputRef?.click()"
                    >
                        <VJoyIcon name="paperclip" size="xsmall" />
                    </button>
                    <input
                        ref="uploadInputRef"
                        type="file"
                        accept="application/pdf"
                        style="display: none"
                        data-testid="advanced-search-document-upload-input"
                        @change="handleUploadFileChange"
                    />
                </template>
                <div v-if="matchMedia.min.Xs" class="advanced-search-submit">
                    <VJoyButton
                        v-if="showSearchCta"
                        variant="main-ai"
                        size="small"
                        :icon="submitIcon"
                        icon-position="right"
                        class="advanced-search-submit__cta"
                        data-testid="advanced-search-submit-button"
                        :loading="loading"
                        :disabled="!canBeSubmitted"
                        :label="t('search.advanced.ai.entrypoint.mode.ai.matching.cta.label.v2')"
                        @click="emit('submit')"
                    />
                </div>
            </div>
        </div>
        <PromptFeedbackMessage v-if="!hidePromptFeedback" :prompt="promptModel" />
        <JobDescriptionInfos v-if="!hideBriefDetailsHelper" :justify="keywordJustifyCenter ? 'center' : 'flex-start'" />
        <VJoyWrapper v-if="matchMedia.max.Xs && showSearchCta" align="flex-end" justify="flex-end" class="joy-mt-4" no-margin>
            <VJoyButton
                variant="main"
                icon="arrow-right"
                icon-position="right"
                data-testid="advanced-search-prompt-search-mobile-button"
                :loading="loading"
                :disabled="!canBeSubmitted"
                :label="t('search.advanced.ai.entrypoint.mode.ai.matching.cta.label.v2')"
                @click="emit('submit')"
            />
        </VJoyWrapper>
    </section>
</template>

<script setup lang="ts">
    import {ref, computed, watch} from 'vue';
    import PromptFeedbackMessage from '@search/components/prompt/PromptFeedbackMessage.vue';
    import {useDropZone} from '@vueuse/core';
    import {storeToRefs} from 'pinia';
    import {useTranslation, useAuth} from '#imports';
    import {useMediaQuery} from '@malt/utils';
    import {VJoyButton, VJoyTextarea, VJoyIcon, VJoyWrapper} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import DocumentButtonInput from './DocumentButtonInput.vue';
    import DocumentPreview from './DocumentPreview.vue';
    import JobDescriptionInfos from './JobDescriptionInfos.vue';
    import {countWords} from '../../utils/text';

    export interface AdvancedSearchPromptContainerProps {
        keywordJustifyCenter?: boolean;
        disableTextArea?: boolean;
        enableUpload?: boolean;
        showSearchCta?: boolean;
        submitIcon?: JoyIconsId;
        shadowFocus?: boolean;
        loading?: boolean;
        rounded?: boolean;
        bottomAlignedActions?: boolean;
        smallBorder?: boolean;
        hideBriefDetailsHelper?: boolean;
        hidePromptFeedback?: boolean;
    }

    const props = withDefaults(defineProps<AdvancedSearchPromptContainerProps>(), {
        enableUpload: false,
        submitIcon: 'sparkle',
        loading: false,
        showSearchCta: true,
        shadowFocus: false,
        disableTextArea: false,
        rounded: false,
        bottomAlignedActions: false,
        smallBorder: false,
        hideBriefDetailsHelper: false,
        hidePromptFeedback: false,
    });

    const promptModel = defineModel<string>();
    const documentUpload = defineModel<File>('documentUpload');

    const emit = defineEmits<{
        (e: 'submit', value?: boolean): void;
    }>();

    const {matchMedia} = useMediaQuery();
    const {t} = useTranslation();
    const {loggedIn} = storeToRefs(useAuth());

    const dragLabel = computed(() =>
        loggedIn.value
            ? t('search.advanced.ai.entrypoint.mode.ai.matching.input.drag.label')
            : t('search.advanced.ai.entrypoint.mode.ai.matching.input.drag.label.unlogged'),
    );

    const MIN_WORDS_TO_ENABLE_SUBMISSION: number = 1;

    const dropZoneRef = ref<HTMLElement | null>(null);
    const uploadInputRef = ref<HTMLInputElement | null>(null);

    const {isOverDropZone} = useDropZone(dropZoneRef, (files: File[] | null) => {
        if (files?.length) {
            documentUpload.value = files[0];
        }
    });

    function handleUploadFileChange(event: Event) {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (file) {
            documentUpload.value = file;
        }
    }

    watch(documentUpload, (val) => {
        if (!val && uploadInputRef.value) {
            uploadInputRef.value.value = '';
        }
    });

    const promptTextareaFocused = ref<boolean>(false);
    const promptWordsCount = computed(() => countWords(promptModel.value));
    const isDragAndDropOverlayDisplay = computed(() => isOverDropZone.value && !documentUpload.value);
    const canBeSubmitted = computed(() => promptWordsCount.value >= MIN_WORDS_TO_ENABLE_SUBMISSION || documentUpload.value);

    const getWrapperStyle = computed(() => ({
        'advanced-search-wrapper': true,
        'advanced-search-wrapper--rounded': props.rounded,
        'advanced-search-wrapper--small-border': props.smallBorder,
        'advanced-search-wrapper--active': promptTextareaFocused.value,
        'advanced-search-wrapper--active-with-shadow': promptTextareaFocused.value && props.shadowFocus,
    }));

    function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            if (canBeSubmitted.value) {
                emit('submit', true);
            }
        }
    }
</script>

<style lang="scss">
    .advanced-search {
        &-controls {
            position: relative;

            .drop-overlay {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.8);
                font-weight: var(--joy-font-weight-bold);
                z-index: 10;
                pointer-events: none;
            }
        }

        &-document-upload-button {
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-5);
            padding: var(--joy-core-spacing-2);
            cursor: pointer;
            display: flex;
            align-items: center;
            box-shadow: 0px 2px 4px 0px #1818180d;
            justify-content: center;
        }

        &-submit {
            flex: 0 0 auto !important;
        }

        &-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--joy-core-spacing-2);
            margin-top: var(--joy-core-spacing-2);

            .advanced-search-submit {
                margin-left: auto;
            }
        }

        &-wrapper {
            border: 2px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-4);
            padding: var(--joy-core-spacing-3) var(--joy-core-spacing-3) var(--joy-core-spacing-3) var(--joy-core-spacing-5);
            margin-bottom: var(--joy-core-spacing-2);
            background-color: var(--joy-color-neutral-0);

            &--soft-shadow {
                box-shadow: 0px 0px 16px 0px #18181814;
            }

            &--active {
                border: 2px solid var(--joy-color-state-focus);
                &-with-shadow {
                    box-shadow: var(--joy-core-elevation-2);
                    border: 2px solid transparent;
                }
            }

            &--rounded {
                border-radius: var(--joy-core-radius-5);
            }

            &--small-border {
                border-width: 1px;

                &.advanced-search-wrapper--active {
                    border-width: 1px;
                }
            }
        }

        &__prompt {
            margin-bottom: var(--joy-core-spacing-2) !important;
            border: 0 !important;
            padding: 0 !important;
            border-radius: var(--joy-core-radius-0) !important;
            min-height: 64px;
        }
    }
</style>
